import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  SelectChangeEvent,
} from '@mui/material';
import { Search, X, ListFilter, RotateCcw } from 'lucide-react';
import { ClienteFiltros as FiltrosState } from '../types';

interface Props {
  filtros: FiltrosState;
  onBuscar: (busqueda: string) => void;
  onFiltrar: (filtros: Partial<FiltrosState>) => void;
  onLimpiar: () => void;
}

export function ClienteFiltros({ filtros, onBuscar, onFiltrar, onLimpiar }: Props) {
  const [inputValue, setInputValue] = useState(filtros.busqueda ?? '');

  // Sincronizar cuando filtros.busqueda cambia externamente (limpiar filtros, etc.)
  useEffect(() => {
    const external = filtros.busqueda ?? '';
    if (external !== inputValue) setInputValue(external);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.busqueda]);

  const mostrarLimpiar = inputValue.length > 0 || filtros.activo !== undefined;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'minmax(420px,1fr) 180px auto' },
        gap: 1.5,
        alignItems: 'center',
        mb: 2,
      }}
    >
      {/* Campo búsqueda */}
      <TextField
        placeholder="Buscar por razón social..."
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          onBuscar(e.target.value);
        }}
        data-testid="busqueda-input"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search size={20} color="#756D77" />
            </InputAdornment>
          ),
          endAdornment: inputValue.length > 0 ? (
            <InputAdornment position="end">
              <Box
                component="button"
                aria-label="Limpiar búsqueda"
                onClick={() => {
                  setInputValue('');
                  onBuscar('');
                }}
                sx={{
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  p: 0,
                  color: '#756D77',
                }}
              >
                <X size={17} />
              </Box>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 48,
            borderRadius: '8px',
            '& fieldset': { borderColor: '#DCD6DD' },
            '&:hover fieldset': { borderColor: '#DCD6DD' },
            '&.Mui-focused fieldset': { borderColor: '#A84F8D' },
          },
        }}
      />

      {/* Selector Estado */}
      <FormControl
        sx={{
          '& .MuiOutlinedInput-root': {
            height: 48,
            borderRadius: '8px',
            '& fieldset': { borderColor: '#DCD6DD' },
            '&:hover fieldset': { borderColor: '#DCD6DD' },
            '&.Mui-focused fieldset': { borderColor: '#A84F8D' },
          },
        }}
      >
        <Select
          value={filtros.activo == null ? '' : String(filtros.activo)}
          onChange={(e: SelectChangeEvent) => {
            const v = e.target.value;
            onFiltrar({ activo: v === '' ? undefined : v === 'true' });
          }}
          data-testid="estado-filter"
          displayEmpty
          startAdornment={
            <InputAdornment position="start">
              <ListFilter size={18} color="#74305F" />
            </InputAdornment>
          }
          renderValue={(value) => {
            if (value === 'true') return 'Activos';
            if (value === 'false') return 'Inactivos';
            return 'Todos';
          }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="true">Activos</MenuItem>
          <MenuItem value="false">Inactivos</MenuItem>
        </Select>
      </FormControl>

      {/* Botón Limpiar condicional */}
      {mostrarLimpiar && (
        <Box
          component="button"
          aria-label="Limpiar filtros"
          onClick={onLimpiar}
          data-testid="limpiar-filtros"
          sx={{
            height: 48,
            px: 2,
            borderRadius: '8px',
            border: '1px solid #D9AACA',
            background: 'transparent',
            color: '#9E427F',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 500,
            whiteSpace: 'nowrap',
            transition: 'background 150ms',
            '&:hover': { background: '#FDF4FB' },
          }}
        >
          <RotateCcw size={18} />
          Limpiar
        </Box>
      )}
    </Box>
  );
}
